/**
 * Video catalog — the single, typed registry of self-hosted videos served
 * across the BizRnR site. Adding a video is one entry here; any page then
 * renders it with `<FacadeVideo videoId="…" />` and emits SEO with
 * `buildVideoObjectSchema(getVideo(id))`. The catalog is storage-agnostic:
 * it only holds public URLs, so the backing store (Cloudflare R2 behind
 * media.bizrnr.com today) can change without touching callers.
 *
 * Durability: every URL is a stable, content-versioned (`/v1/`) path under
 * the first-party media.bizrnr.com domain. Bumping the version (`…/v2/…`)
 * cleanly busts the immutable CDN cache on a re-cut without changing the
 * domain Google has indexed.
 */

/** First-party media origin (Cloudflare R2 custom domain, zero-egress edge). */
export const MEDIA_ORIGIN = 'https://media.bizrnr.com';

export interface VideoSource {
  /** Absolute, publicly fetchable URL. MP4 should be faststart (moov atom at front). */
  readonly src: string;
  readonly type: 'video/mp4' | 'video/webm';
}

export interface VideoCatalogEntry {
  /** Stable lookup id (kebab-case). */
  readonly id: string;
  /** Human title — used as the VideoObject `name`. */
  readonly title: string;
  /** One-paragraph description — used as the VideoObject `description`. */
  readonly description: string;
  /** Public poster/thumbnail URL (also the VideoObject `thumbnailUrl`). */
  readonly poster: string;
  /** Ordered playable sources; the browser picks the first it supports. */
  readonly sources: readonly VideoSource[];
  /** Public WebVTT captions URL → rendered as a `<track kind="captions">`. */
  readonly captionsVtt?: string;
  /** Spoken-content paragraphs — rendered as an on-page transcript (AEO + a11y). */
  readonly transcript?: readonly string[];
  /** Exact duration in seconds (ffprobe-verified) → ISO-8601 in the schema. */
  readonly durationSeconds: number;
  /** Native pixel dimensions of the primary source. */
  readonly width: number;
  readonly height: number;
  /** Aspect ratio — drives the facade frame so there is zero layout shift. */
  readonly aspect: '16:9' | '9:16' | '1:1';
  /** RFC 3339 upload/publish datetime with timezone — required by Google for video rich results. */
  readonly uploadDate: string;
  /** Optional public mirror (e.g. YouTube) surfaced as schema `sameAs` for reach. */
  readonly youtubeUrl?: string;
}

const HOMEPAGE_EXPLAINER_V1 = `${MEDIA_ORIGIN}/video/homepage-explainer/v1`;

/**
 * Verbatim narration (brand-normalized to "Bri"; the spoken VO says "Bree").
 * Doubles as the on-page transcript and the source of truth for the captions
 * track, so answer engines and screen readers get the full spoken content.
 */
const HOMEPAGE_EXPLAINER_TRANSCRIPT: readonly string[] = [
  "Your business just missed another call — and that's real money walking out the door.",
  'Missed calls, texts, and DMs are quietly costing you a fortune. Most people never call back — they just call your competitor.',
  'Meet Bri — Business Runner Intelligence. She answers every call, text, and DM, all day, all night, 24/7/365.',
  'She picks up on the first ring, talks just like a real person, and books the appointment right there on the call.',
  'Bri qualifies every lead, books it straight into your calendar, and texts you the details — instantly.',
  'No call goes to voicemail. No message goes unanswered. Every single one — handled.',
  'She even speaks any language your customers do.',
  "And Bri gets you found on ChatGPT, Claude, and Gemini — so when people ask AI who to call, you're the answer.",
  'Across thousands of calls, nearly every one is caught — and booked in minutes.',
  "And it's this easy: sign up, forward your business phone, and every call is answered. You're live in sixty seconds.",
  'All for just ninety-nine dollars a month. No contracts — cancel anytime.',
  'Stop missing customers — and let Bri answer every call. Get started now.',
];

export const VIDEO_CATALOG: readonly VideoCatalogEntry[] = [
  {
    id: 'homepage-explainer',
    title: 'How BizRnR answers every call, text, and DM — 24/7',
    description:
      'A 97-second explainer: how the BizRnR AI receptionist (Bri) answers every call, text, and DM on the first ring, qualifies leads, books appointments on your calendar, speaks any language, and gets you found on ChatGPT, Claude, and Gemini — set up in 60 seconds for $99/month.',
    poster: `${HOMEPAGE_EXPLAINER_V1}/poster.jpg`,
    sources: [{ src: `${HOMEPAGE_EXPLAINER_V1}/explainer-16x9.mp4`, type: 'video/mp4' }],
    captionsVtt: `${HOMEPAGE_EXPLAINER_V1}/captions.en.vtt`,
    transcript: HOMEPAGE_EXPLAINER_TRANSCRIPT,
    durationSeconds: 96.93,
    width: 1920,
    height: 1080,
    aspect: '16:9',
    // YouTube's published timestamp for WZDyQ_l1Mfk (2026-06-30 17:17:41 PDT).
    uploadDate: '2026-07-01T00:17:41Z',
    // Published to the BizRnR YouTube channel → surfaced as VideoObject `sameAs` for reach.
    youtubeUrl: 'https://youtu.be/WZDyQ_l1Mfk',
  },
];

const VIDEO_BY_ID: ReadonlyMap<string, VideoCatalogEntry> = new Map(
  VIDEO_CATALOG.map((v) => [v.id, v]),
);

/** Look up a catalog entry by id. Returns `undefined` if not registered. */
export function getVideo(id: string): VideoCatalogEntry | undefined {
  return VIDEO_BY_ID.get(id);
}

/** Numeric aspect ratio (width / height) for the catalog entry's `aspect`. */
export function aspectRatioValue(aspect: VideoCatalogEntry['aspect']): number {
  const [w, h] = aspect.split(':').map(Number);
  return w / h;
}
